import { Component, OnInit, OnDestroy, inject, computed, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TeamService } from '../../core/services/team.service';
import { CustomClaimService } from '../../core/services/custom-claim.service';
import { OrganizationService } from '../../core/services/organization.service';
import { AuthService } from '../../core/services/auth.service';
import { InviteMemberModalComponent } from './invite-member-modal.component';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import {
  OrganizationInvitation,
  MembershipRole,
  RoleHelper
} from '../../core/models/team.model';

/**
 * Team Management Page Component
 *
 * Displays organization team members and pending invitations.
 * Allows Owner and Chief Supervisor to invite, resend, and cancel invitations.
 *
 * Reference: Task Group 8 - Team Management UI - Invite Member Flow
 * Spec: /agent-os/specs/2026-01-22-user-onboarding/planning/spec.md
 */
@Component({
  selector: 'app-team',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InviteMemberModalComponent,
    ConfirmModalComponent
  ],
  templateUrl: './team.component.html',
  styleUrls: ['./team.component.scss']
})
export class TeamComponent implements OnInit, OnDestroy {
  public teamService = inject(TeamService);
  private customClaimService = inject(CustomClaimService);
  private organizationService = inject(OrganizationService);
  private authService = inject(AuthService);

  @ViewChild('inviteModal') inviteModal!: InviteMemberModalComponent;
  @ViewChild('confirmModal') confirmModal!: ConfirmModalComponent;

  // Current organization ID
  // TODO: Replace with actual organization context when multi-org support is added
  readonly currentOrganizationId = signal<string | null>(null);

  // User permissions - check org membership roles (Owner or ChiefSupervisor)
  readonly canInviteMembers = computed(() => {
    const orgId = this.currentOrganizationId();
    if (!orgId) return false;
    return this.customClaimService.isOwnerOrChiefSupervisor(orgId);
  });

  // Data from service
  readonly members = computed(() => this.teamService.members());
  readonly invitations = computed(() => this.teamService.invitations());
  readonly isLoading = computed(() => this.teamService.isLoading());
  readonly error = computed(() => this.teamService.error());

  // Computed: pending invitations only
  readonly pendingInvitations = computed(() =>
    this.invitations().filter(i =>
      !i.isAccepted &&
      !i.isCancelled &&
      !RoleHelper.isExpired(i.expiresAt)
    )
  );

  // Computed: active members only
  readonly activeMembers = computed(() =>
    (this.members() || []).filter(m => !m.deletedAt)
  );

  // Toast message
  toastMessage = signal<string | null>(null);
  toastType = signal<'success' | 'error' | 'info'>('success');

  // Action tracking
  private invitationToCancel: OrganizationInvitation | null = null;
  private processingInvitationId = signal<string | null>(null);

  async ngOnInit(): Promise<void> {
    // First try to get organization from custom claims
    let orgIds = this.customClaimService.getOrganizationIds();
    if (orgIds.length > 0) {
      this.currentOrganizationId.set(orgIds[0]);
      await this.loadData();
      return;
    }

    // Claims are empty - try refreshing the Firebase token first
    // This handles the case where user's Firebase token hasn't been refreshed
    // after organization creation or role changes
    try {
      await this.authService.refreshToken();
      // Check claims again after refresh
      orgIds = this.customClaimService.getOrganizationIds();
      if (orgIds.length > 0) {
        this.currentOrganizationId.set(orgIds[0]);
        await this.loadData();
        return;
      }
    } catch (error) {
      console.warn('Token refresh failed, falling back to API:', error);
    }

    // Fallback: Get organization from API if claims are still empty
    try {
      const organization = await this.organizationService.getMyOrganization();
      if (organization) {
        this.currentOrganizationId.set(organization.id);
        await this.loadData();
      } else {
        this.showToast('No organization found. Please contact support.', 'error');
      }
    } catch (error) {
      console.error('Error loading organization:', error);
      this.showToast('No organization found. Please contact support.', 'error');
    }
  }

  ngOnDestroy(): void {
    this.teamService.reset();
  }

  /**
   * Load team data (members and invitations)
   */
  async loadData(): Promise<void> {
    const orgId = this.currentOrganizationId();
    if (!orgId) {
      console.error('No organization ID available');
      return;
    }

    try {
      // Load both members and invitations
      await Promise.all([
        this.teamService.loadMembers(orgId, false),
        this.teamService.loadInvitations(orgId, 'pending')
      ]);
    } catch (error) {
      console.error('Error loading team data:', error);
    }
  }

  /**
   * Load pending invitations
   */
  async loadInvitations(): Promise<void> {
    const orgId = this.currentOrganizationId();
    if (!orgId) {
      console.error('No organization ID available');
      return;
    }

    try {
      await this.teamService.loadInvitations(orgId, 'pending');
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  }

  /**
   * Open invite member modal
   */
  openInviteModal(): void {
    const orgId = this.currentOrganizationId();
    if (!orgId) {
      this.showToast('Organization not found', 'error');
      return;
    }

    if (!this.canInviteMembers()) {
      this.showToast('You do not have permission to invite members', 'error');
      return;
    }

    this.inviteModal.open(orgId);
  }

  /**
   * Handle invitation created event
   */
  async onInvitationCreated(): Promise<void> {
    this.showToast('Invitation sent successfully!', 'success');
    await this.loadData();
  }

  /**
   * Resend invitation
   */
  async resendInvitation(invitation: OrganizationInvitation): Promise<void> {
    const orgId = this.currentOrganizationId();
    if (!orgId) return;

    // Check rate limiting
    if (!RoleHelper.canResend(invitation.lastResentAt, invitation.resendCount)) {
      if (invitation.resendCount >= 5) {
        this.showToast('Maximum resend limit (5) reached for this invitation', 'error');
      } else {
        this.showToast('Please wait 24 hours before resending this invitation', 'error');
      }
      return;
    }

    this.processingInvitationId.set(invitation.id);

    try {
      await this.teamService.resendInvitation(orgId, invitation.id);
      this.showToast(`Invitation resent to ${invitation.email}`, 'success');
    } catch (error: any) {
      const errorMsg = error.error?.error || error.message || 'Failed to resend invitation';
      this.showToast(errorMsg, 'error');
    } finally {
      this.processingInvitationId.set(null);
    }
  }

  /**
   * Cancel invitation (show confirmation)
   */
  cancelInvitation(invitation: OrganizationInvitation): void {
    this.invitationToCancel = invitation;
    this.confirmModal.open({
      title: 'Cancel Invitation',
      message: `Are you sure you want to cancel the invitation to ${invitation.email}?`,
      confirmText: 'Cancel Invitation',
      cancelText: 'Keep Invitation',
      confirmButtonClass: 'btn-danger',
      icon: 'bi-x-circle'
    });
  }

  /**
   * Confirm cancel invitation
   */
  async onCancelConfirmed(): Promise<void> {
    if (!this.invitationToCancel) return;

    const orgId = this.currentOrganizationId();
    if (!orgId) return;

    const invitation = this.invitationToCancel;
    this.processingInvitationId.set(invitation.id);

    try {
      await this.teamService.cancelInvitation(orgId, invitation.id);
      this.showToast('Invitation cancelled', 'success');
    } catch (error: any) {
      const errorMsg = error.error?.error || error.message || 'Failed to cancel invitation';
      this.showToast(errorMsg, 'error');
    } finally {
      this.processingInvitationId.set(null);
      this.invitationToCancel = null;
    }
  }

  /**
   * Handle cancel cancelled
   */
  onCancelCancelled(): void {
    this.invitationToCancel = null;
  }

  /**
   * Copy invitation link to clipboard
   */
  async copyInvitationLink(invitation: OrganizationInvitation): Promise<void> {
    // Note: In production, this would generate the actual invitation link
    // For now, we'll just show a placeholder
    const invitationUrl = `${window.location.origin}/accept-invitation?token=${invitation.id}`;

    try {
      await navigator.clipboard.writeText(invitationUrl);
      this.showToast('Invitation link copied to clipboard', 'success');
    } catch (error) {
      this.showToast('Failed to copy link to clipboard', 'error');
    }
  }

  /**
   * Get role names for invitation
   */
  getRoleNames(role: number): string[] {
    return RoleHelper.getRoleNames(role);
  }

  /**
   * Get role badge class
   */
  getRoleBadgeClass(roleName: string): string {
    return RoleHelper.getRoleBadgeClass(roleName);
  }

  /**
   * Get days remaining for invitation
   */
  getDaysRemaining(expiresAt: Date | string): number {
    return RoleHelper.getDaysRemaining(expiresAt);
  }

  /**
   * Get expiration display text
   */
  getExpirationDisplay(expiresAt: Date | string): string {
    const daysRemaining = this.getDaysRemaining(expiresAt);

    if (daysRemaining <= 0) {
      return 'Expired';
    } else if (daysRemaining === 1) {
      return '1 day left';
    } else {
      return `${daysRemaining} days left`;
    }
  }

  /**
   * Get expiration CSS class
   */
  getExpirationClass(expiresAt: Date | string): string {
    const daysRemaining = this.getDaysRemaining(expiresAt);

    if (daysRemaining <= 0) {
      return 'text-danger';
    } else if (daysRemaining <= 2) {
      return 'text-warning';
    } else {
      return 'text-muted';
    }
  }

  /**
   * Check if invitation can be resent
   */
  canResend(invitation: OrganizationInvitation): boolean {
    return RoleHelper.canResend(invitation.lastResentAt, invitation.resendCount);
  }

  /**
   * Get resend disabled reason
   */
  getResendDisabledReason(invitation: OrganizationInvitation): string {
    if (invitation.resendCount >= 5) {
      return 'Maximum resend limit reached';
    }
    if (!this.canResend(invitation)) {
      return 'Please wait 24 hours before resending';
    }
    return '';
  }

  /**
   * Check if invitation is being processed
   */
  isProcessing(invitationId: string): boolean {
    return this.processingInvitationId() === invitationId;
  }

  /**
   * Show toast message
   */
  private showToast(message: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.toastMessage.set(message);
    this.toastType.set(type);

    // Auto-hide after 5 seconds
    setTimeout(() => {
      this.toastMessage.set(null);
    }, 5000);
  }

  /**
   * Close toast
   */
  closeToast(): void {
    this.toastMessage.set(null);
  }
}
